import type { SVGProps } from 'react'

/**
 * The Translate mark: the Hebrew letter א (aleph).
 *
 * baba translates Hebrew — the previous two-script "A / 文" glyph named a
 * language pair the product does not serve and read as generic machine
 * translation. Aleph is the first letter of the alphabet people come here to
 * read, so it carries the brand and the destination at once.
 *
 * Rendered as the real glyph from the app's own Hebrew face rather than as
 * hand-drawn paths. Aleph is a curved, asymmetric letterform; every stroked
 * approximation of it (a diagonal plus two yods) collapses into a symmetric
 * "✗" at 13–24px, which in a nav rail reads as close/delete — the exact
 * wrong meaning next to "Translate". A real letter cannot misread as a
 * different symbol.
 *
 * `--font-hebrew` is the design-system token (packages/tokens/src/tokens.css)
 * and Heebo Variable is imported at app start in src/main.tsx, so the face is
 * present rather than left to whatever the viewer's system resolves; the
 * token's own fallback chain covers the rest. It has to go through inline
 * `style` because an SVG presentation ATTRIBUTE does not resolve `var()`.
 *
 * Weight 500 at 23/24 of the box puts the glyph's stems at roughly the 1.9px
 * stroke of the surrounding icon set and gives it the same optical presence
 * as the stroked icons beside it, in a box that matches theirs so it drops
 * into the same 18px slot.
 */
export function HebrewTranslateIcon({
  size = 24,
  weight = 500,
  ...rest
}: Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> & { size?: number; weight?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      {...rest}
    >
      <text
        x="12"
        y="12.5"
        textAnchor="middle"
        dominantBaseline="central"
        fill="currentColor"
        stroke="none"
        style={{
          fontFamily: "var(--font-hebrew, 'Heebo Variable', 'Heebo', 'Noto Sans Hebrew', system-ui, sans-serif)",
          fontSize: 23,
          fontWeight: weight,
        }}
      >
        א
      </text>
    </svg>
  )
}

export default HebrewTranslateIcon
